import { useTranslation } from 'react-i18next'
import { useQuery } from 'convex/react'
import { Navigate, Link } from 'react-router-dom'
import { api } from '../../convex/_generated/api'
import { useAuth0 } from '../hooks/useAuth0'
import { Header } from '../components/layout/Header'
import { LovedOneCard } from '../components/dashboard/LovedOneCard'

/**
 * Dashboard - Caregiver dashboard landing page
 *
 * Routing logic:
 * - 0 loved ones: Redirect to voice onboarding
 * - 1 loved one: Show detail view directly (LovedOneDetail)
 * - 2+ loved ones: Show list view with LovedOneCards (bank-style)
 */
export function Dashboard() {
  const { t } = useTranslation()
  const { user } = useAuth0()
  const lovedOnes = useQuery(api.dashboard.getLovedOnes)

  const containerStyles: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: '#f8f9fa',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  }

  const contentWrapperStyles: React.CSSProperties = {
    padding: '1.5rem 1rem',
    maxWidth: '600px',
    margin: '0 auto',
  }

  const pageTitleStyles: React.CSSProperties = {
    fontSize: 'clamp(24px, 4vw, 32px)',
    fontWeight: 700,
    color: '#1a1a1a',
    marginBottom: '1.5rem',
  }

  const loadingStyles: React.CSSProperties = {
    textAlign: 'center',
    padding: '2rem',
    color: '#666',
  }

  const lovedOnesListStyles: React.CSSProperties = {
    display: 'grid',
    gap: '1rem',
  }

  const addLinkStyles: React.CSSProperties = {
    display: 'block',
    textAlign: 'center',
    padding: '1rem',
    color: '#0066cc',
    textDecoration: 'none',
    fontSize: '14px',
    marginTop: '1rem',
  }

  // Loading state
  if (!user || lovedOnes === undefined) {
    return (
      <div style={containerStyles}>
        <Header />
        <div style={contentWrapperStyles}>
          <p style={loadingStyles}>{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  // No loved ones - redirect to onboarding
  if (lovedOnes.length === 0) {
    return <Navigate to="/voice-onboarding" replace />
  }

  // Single loved one - redirect to detail view
  if (lovedOnes.length === 1) {
    return <Navigate to={`/dashboard/${lovedOnes[0].lovedOne._id}`} replace />
  }

  // Multiple loved ones - show list view (bank-style pattern)
  return (
    <div style={containerStyles}>
      <Header />
      <div style={contentWrapperStyles}>
        <h1 style={pageTitleStyles}>{t('dashboard.yourLovedOnes')}</h1>

        <div style={lovedOnesListStyles}>
          {lovedOnes.map(item => (
            <LovedOneCard
              key={item.lovedOne._id}
              lovedOne={item.lovedOne}
              callsThisWeek={item.metrics.callsThisWeek}
            />
          ))}
        </div>

        <Link to="/settings" style={addLinkStyles}>
          {t('dashboard.addLovedOne')}
        </Link>
      </div>
    </div>
  )
}
